import { createSlice } from '@reduxjs/toolkit';

import {
  NetworkPrinter,
  Printer,
  PRINTER_KINDS,
  PRINTER_STATUSES,
  PrintPreview,
  UsbPrinter,
  UsbVendorDriverPrinter,
} from '~/main/printer/types';

import type { CaseReducer, PayloadAction } from '@reduxjs/toolkit';
import type {
  ConnectionPrinters,
  DefaultPrinters,
  PrintersState,
} from '~/renderer/store/printers/types';

const initialState: PrintersState = {
  NETWORK: [],
  USB: [],
  default: {},
  preview: undefined,
};

interface DefaultPrinterStatus {
  kind: PRINTER_KINDS;
  status: PRINTER_STATUSES;
}

const addPrinter: CaseReducer<
  PrintersState,
  PayloadAction<{ printer: NetworkPrinter | UsbPrinter | UsbVendorDriverPrinter }>
> = (state, action) => {
  const { printer } = action.payload;

  return {
    ...state,
    [printer.connection]: (state[printer.connection] as Printer[])
      .filter(p => p.id !== printer.id)
      .concat(printer),
  };
};

const addDefaultPrinter: CaseReducer<
  PrintersState,
  PayloadAction<{ kind: PRINTER_KINDS; printer: Printer }>
> = (state, action) => {
  const { kind, printer } = action.payload;
  state.default[kind] = printer;
};

const removeDefaultPrinter: CaseReducer<PrintersState, PayloadAction<{ kind: PRINTER_KINDS }>> = (
  state,
  action,
) => {
  delete state.default[action.payload.kind];
};

const updatePrinters: CaseReducer<PrintersState, PayloadAction<Partial<ConnectionPrinters>>> = (
  state,
  action,
) => {
  return { ...state, ...action.payload };
};

const updateDefaultPrinters: CaseReducer<PrintersState, PayloadAction<DefaultPrinters>> = (
  state,
  action,
) => {
  state.default = {
    ...state.default,
    ...action.payload,
  };
};

const updateDefaultPrinterStatus: CaseReducer<
  PrintersState,
  PayloadAction<DefaultPrinterStatus>
> = (state, action) => {
  if (!state.default[action.payload.kind]) return;
  state.default[action.payload.kind]!.status = action.payload.status;
};

const deleteDefaultPrinter: CaseReducer<PrintersState> = state => {
  state.default = {};
};

const updatePrintersPreview: CaseReducer<PrintersState, PayloadAction<PrintPreview | undefined>> = (
  state,
  action,
) => {
  state.preview = action.payload;
};

const printersSlice = createSlice({
  name: 'printers',
  initialState,
  reducers: {
    addPrinter,
    addDefaultPrinter,
    removeDefaultPrinter,
    updatePrinters,
    updateDefaultPrinters,
    updateDefaultPrinterStatus,
    updatePrintersPreview,
    deleteDefaultPrinter,
  },
});

const { actions, reducer } = printersSlice;
export { reducer as printersReducer, actions as printersActions };
